import * as CookieConsent from "vanilla-cookieconsent";
import googleTagManager from "./googleTagManager/GoogleTagManager";

const loggedConsents = new Set<string>();

let cookieConsentInitialized = false;

const logConsentToDatabase = async (
	consentId: string,
	consentGiven: boolean,
	consentPurpose: string,
	methodOfConsent: string,
	legalBasis: string = "consent"
): Promise<void> => {
	const logKey = `${consentId}-${consentPurpose}-${methodOfConsent}-${consentGiven}`;

	const isPreferenceChange = methodOfConsent === "preferences_modal_change";

	if (!isPreferenceChange && loggedConsents.has(logKey)) {
		return;
	}

	if (!isPreferenceChange) {
		loggedConsents.add(logKey);
	}

	try {
		const consentData = {
			consent_id: consentId,
			consent_given: consentGiven,
			consent_purpose: consentPurpose,
			legal_basis: legalBasis,
			method_of_consent: methodOfConsent,
			privacy_policy_version: "1.0",
			third_party_sharing: consentPurpose === "necessary" ? "none" : "google",
			retention_period: "2 years",
		};

		const response = await fetch("/cookieConsent.php", {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify(consentData),
		});

		if (!response.ok) {
			throw new Error(`HTTP error! status: ${response.status}`);
		}

		const result = await response.json();
	} catch (error) {
		if (!isPreferenceChange) {
			loggedConsents.delete(logKey);
		}
	}
};

const cookies = () => {
	if (cookieConsentInitialized) {
		return;
	}

	cookieConsentInitialized = true;

	CookieConsent.run({
		guiOptions: {
			consentModal: {
				layout: "bar",
				position: "bottom",
				equalWeightButtons: true,
				flipButtons: false,
			},
			preferencesModal: {
				layout: "box",
				position: "left",
				equalWeightButtons: true,
				flipButtons: false,
			},
		},
		disablePageInteraction: false,
		categories: {
			necessary: {
				enabled: true,
				readOnly: true,
			},
			analytics: {
				enabled: false,
			},
			ad_storage: {
				enabled: false,
			},
			ad_user_data: {
				enabled: false,
			},
			ad_personalization: {
				enabled: false,
			},
		},
		language: {
			default: "en",
			translations: {
				en: {
					consentModal: {
						title: "We value your privacy",
						description:
							"This website uses cookies to enhance your browsing experience and analyse site traffic. You can choose which categories of cookies to accept. Essential cookies are necessary for core site functionality and are always active. All other cookies are optional and will only be used with your consent.",
						acceptAllBtn: "Accept all cookies",
						acceptNecessaryBtn: "Accept essential cookies only",
						showPreferencesBtn: "Manage preferences",
						footer:
							'<a href="/cookies">Cookie Policy</a>\n<a href="/privacy">Privacy Policy</a>',
					},
					preferencesModal: {
						title: "Manage Preferences",
						acceptAllBtn: "Accept all cookies",
						acceptNecessaryBtn: "Accept essential cookies only",
						savePreferencesBtn: "Save preferences",
						closeIconLabel: "Close modal",
						serviceCounterLabel: "Service|Services",
						sections: [
							{
								title: "Cookie Usage",
								description:
									'<p>We use cookies and similar technologies to improve your experience on our website, analyse traffic, and serve relevant content. You can manage your preferences at any time by returning to this page.<br /><br />Some cookies are strictly necessary and always active. Others are optional and will only be set if you give us your consent.<br /><br />You can learn more by reading our <a href="/privacy/" title="Read privacy notice">Privacy Notice</a> and <a href="/cookies/" title="Read more about cookies">Cookie Policy</a></p>',
							},
							{
								title:
									'Strictly Necessary Cookies <span class="pm__badge">Always Active</span>',
								description:
									"These cookies are essential for the proper functioning of the website and cannot be disabled. They enable basic features such as page navigation and access to secure areas. This website does not use cookies for essential functionality, but basic operations may still rely on similar technologies.",
								linkedCategory: "necessary",
							},
							{
								title: "Google Analytics",
								description:
									"We use Google Analytics to understand how visitors interact with our site - such as which pages are visited most often. This helps us improve the website for all users. No personally identifying information is collected without your consent.",
								linkedCategory: "analytics",
							},
							{
								title: "Google Ads - Advertising Cookies",
								description:
									"These cookies are used to serve targeted ads based on your interests and measure the performance of our ad campaigns. They help ensure our advertising is relevant and effective.",
								linkedCategory: "ad_storage",
							},
							{
								title: "Google Ads - Send User Data",
								description:
									"These cookies collect data on your interactions with Google Ads, such as ad clicks or completed actions like purchases or sign-ups. This helps us understand which ads are working.",
								linkedCategory: "ad_user_data",
							},
							{
								title: "Google Ads - Ads Personalisation",
								description:
									"These cookies personalise the ads you see based on your behaviour and interests, aiming to provide a more relevant advertising experience.",
								linkedCategory: "ad_personalization",
							},
						],
					},
				},
			},
		},
		onConsent: ({ cookie }) => {
			googleTagManager.denyConsent("analytics_storage");
			googleTagManager.denyConsent("ad_storage");
			googleTagManager.denyConsent("ad_user_data");
			googleTagManager.denyConsent("ad_personalization");

			logConsentToDatabase(
				cookie.consentId,
				true,
				"necessary",
				"cookie_banner_initial",
				"legitimate_interest"
			);

			const analyticsAccepted = cookie.categories.includes("analytics");
			if (analyticsAccepted) {
				googleTagManager.grantConsent("analytics_storage");
			}
			logConsentToDatabase(
				cookie.consentId,
				analyticsAccepted,
				"analytics_storage",
				"cookie_banner_initial"
			);

			const adStorageAccepted = cookie.categories.includes("ad_storage");
			if (adStorageAccepted) {
				googleTagManager.grantConsent("ad_storage");
			}
			logConsentToDatabase(
				cookie.consentId,
				adStorageAccepted,
				"ad_storage",
				"cookie_banner_initial"
			);

			const adUserDataAccepted = cookie.categories.includes("ad_user_data");
			if (adUserDataAccepted) {
				googleTagManager.grantConsent("ad_user_data");
			}
			logConsentToDatabase(
				cookie.consentId,
				adUserDataAccepted,
				"ad_user_data",
				"cookie_banner_initial"
			);

			const adPersonalizationAccepted =
				cookie.categories.includes("ad_personalization");
			if (adPersonalizationAccepted) {
				googleTagManager.grantConsent("ad_personalization");
			}
			logConsentToDatabase(
				cookie.consentId,
				adPersonalizationAccepted,
				"ad_personalization",
				"cookie_banner_initial"
			);

			googleTagManager.setPromptSeen();
		},
		onChange: ({ cookie, changedCategories }) => {
			const analyticsAccepted = cookie.categories.includes("analytics");
			if (analyticsAccepted) {
				googleTagManager.grantConsent("analytics_storage");
			} else {
				googleTagManager.denyConsent("analytics_storage");
			}

			logConsentToDatabase(
				cookie.consentId,
				analyticsAccepted,
				"analytics_storage",
				"preferences_modal_change"
			);

			const adStorageAccepted = cookie.categories.includes("ad_storage");
			if (adStorageAccepted) {
				googleTagManager.grantConsent("ad_storage");
			} else {
				googleTagManager.denyConsent("ad_storage");
			}

			logConsentToDatabase(
				cookie.consentId,
				adStorageAccepted,
				"ad_storage",
				"preferences_modal_change"
			);

			const adUserDataAccepted = cookie.categories.includes("ad_user_data");
			if (adUserDataAccepted) {
				googleTagManager.grantConsent("ad_user_data");
			} else {
				googleTagManager.denyConsent("ad_user_data");
			}

			logConsentToDatabase(
				cookie.consentId,
				adUserDataAccepted,
				"ad_user_data",
				"preferences_modal_change"
			);

			const adPersonalizationAccepted =
				cookie.categories.includes("ad_personalization");
			if (adPersonalizationAccepted) {
				googleTagManager.grantConsent("ad_personalization");
			} else {
				googleTagManager.denyConsent("ad_personalization");
			}

			logConsentToDatabase(
				cookie.consentId,
				adPersonalizationAccepted,
				"ad_personalization",
				"preferences_modal_change"
			);
		},
	});
};

export default cookies;
